/**
 * Colour swatch resolution, shared by the PDP option selector (`product-option.js`)
 * and the product card option selector (`product-card-option.js`).
 *
 * A colour's swatch is resolved in this order:
 *
 *   1. `color` metaobject image — the theme's own option metaobject, if it is ever
 *      created (registered in `snippets/product-options-config.liquid`, the same way
 *      metal/gemstone/zodiac swatches work).
 *   2. Store Color metaobject — Shopify's standard `shopify--color-pattern`
 *      (Content → Metaobjects → Color), published by `snippets/color-swatch-data.liquid`.
 *      Its image wins over its hex. **This is the merchant-editable source of truth.**
 *   3. Built-in palette below — covers colours with no metaobject entry yet.
 *   4. Nothing: callers render a neutral dot, and the colour's name still shows.
 *
 * The palette is needed because `custom.color` is a plain
 * `list.single_line_text_field` on the product — it stores the colour's name and
 * nothing else. Adding an entry to the Color metaobject is preferred over adding a
 * hex here, since it needs no deploy.
 *
 * @module color-swatches
 */
(function () {
  window.theme = window.theme || {};

  /**
   * Fallback palette, keyed by handleized colour name. A value is one hex (solid dot)
   * or two (split dot, e.g. two-tone leather). Superseded per-colour by a Color
   * metaobject entry.
   */
  window.theme.colorSwatches = {
    'beige': '#E8DCC8',
    'black': '#1C1C1C',
    'blue': '#3B5A9D',
    'brown': '#7B5233',
    'burgundy': '#6E1B2C',
    'champagne': '#EBD7B5',
    'cream': '#F4EBDD',
    'ebony': '#3B322C',
    'gold': '#D4AF37',
    'gray': '#9A9A9A',
    'green': '#3F6B4B',
    'grey': '#9A9A9A',
    'ivory': '#F6F1E4',
    'mahogany': '#6B3A2A',
    'mauve': '#C0A0A8',
    'navy': '#22304F',
    'oak': '#C6A164',
    'orange': '#DE7A3C',
    'pink': '#EFC0C4',
    'purple': '#7C5C9E',
    'red': '#A62534',
    'rose-gold': '#B76E79',
    'silver': '#C9C9C9',
    'tan': '#C8A67E',
    'teal': '#2F7F86',
    'walnut': '#5C4033',
    'white': '#FFFFFF',
    'yellow': '#E7C74B',
    'yellow-gold': '#D4AF37',
    'white-gold': '#E3E3E3',
  };

  /**
   * The store's Color metaobject entry for a colour, if there is one.
   *
   * @param {string} optionHandle - handleized option value, e.g. "rose-gold"
   * @returns {{color?: string, image?: string}|null}
   */
  function storeColorEntry(optionHandle) {
    const entries = window.theme.shopifyColorSwatches;
    return entries && entries[optionHandle] ? entries[optionHandle] : null;
  }

  /**
   * CSS `background` value for a colour, or null when the colour is unknown
   * (callers render a neutral dot in that case). The store's Color metaobject wins
   * over the built-in palette, so editing a hex in the admin changes the storefront.
   *
   * @param {string} optionHandle - handleized option value, e.g. "rose-gold"
   * @returns {string|null}
   */
  window.theme.getColorSwatchBackground = function (optionHandle) {
    const entry = storeColorEntry(optionHandle);
    if (entry && entry.color) return entry.color;

    const swatch = window.theme.colorSwatches[optionHandle];
    if (!swatch) return null;
    return Array.isArray(swatch)
      ? `linear-gradient(135deg, ${swatch[0]} 0 50%, ${swatch[1]} 50% 100%)`
      : swatch;
  };

  /**
   * Matches the handleize() used by product-option.js / product-card-option.js, so
   * metaobject names key the same way option values do. Deliberately not
   * `theme.handleize` — that one keeps `&` and does not collapse double spaces.
   *
   * @param {string} value
   * @returns {string}
   */
  function handleizeOptionValue(value) {
    return String(value)
      .toLowerCase()
      .replaceAll('  ', ' ')
      .replaceAll('&', '')
      .replaceAll(' ', '-')
      .replaceAll('--', '-');
  }

  /**
   * Image URL for a colour: the theme's own `color` metaobject first (emitted into
   * `defaultProductOptions` by `product-option-setup`), then the store's Color
   * metaobject image.
   *
   * @param {string} optionHandle - handleized option value, e.g. "pink"
   * @returns {string|null}
   */
  window.theme.getColorSwatchImage = function (optionHandle) {
    const defaults = window.theme.defaultProductOptions || [];
    const colorOption = defaults.find((option) => option.handle === 'color');

    if (colorOption) {
      const match = (colorOption.options || []).find((value) => (
        value && value.name && handleizeOptionValue(value.name) === optionHandle
      ));
      if (match && match.image) return match.image;
    }

    const entry = storeColorEntry(optionHandle);
    return entry && entry.image ? entry.image : null;
  };
})();
